import Slider from '@react-native-community/slider';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';

import type { AudioPlayerWithContextProps } from './audio-player-with-context';
import styles from './audio-player.style';
import { PlayerButton } from './player-button/player-button';
import { SlowButton } from './slow-button/slow-button';
import formatMillisecondsToAudioTime from './utils/format-milliseconds-to-audio-time';
import type { UseSound } from '../../hooks/use-sound';
import ThemedText2 from '../themed-text-2';

export interface AudioPlayerProps extends AudioPlayerWithContextProps {
  sound: UseSound;
}

export function AudioPlayer({
  activeColor,
  inactiveColor,
  barContainerColor,
  barActiveColor,
  isTextProgressVisible = false,
  containerStyle,
  onReproduce,
  isSlowButtonVisible = false,
  sound,
}: AudioPlayerProps) {
  const {
    status,
    togglePlay,
    sliderValue,
    setProgress,
    duration: soundDuration,
  } = sound;
  const [isTouchingSlider, setIsTouchingSlider] = useState(false);
  const [activeSliderValue, setActiveSliderValue] = useState(0);

  useEffect(() => {
    if (onReproduce && sliderValue === 1) {
      setTimeout(onReproduce, 700);
    }
  }, [sliderValue, onReproduce]);

  const activateSlider = (value: number) => {
    setIsTouchingSlider(true);
    setActiveSliderValue(value);
  };

  const changeProgress = (value: number) => {
    setProgress(value);
    setIsTouchingSlider(false);
    setActiveSliderValue(0);
  };

  const sliderCurrentValue = isTouchingSlider ? activeSliderValue : sliderValue;

  return (
    <View style={[styles.container, containerStyle]}>
      <PlayerButton
        isLoading={!status.isLoaded}
        style={styles.playerButton}
        isPlaying={status.isLoaded && status.isPlaying}
        onPress={togglePlay}
        {...{ activeColor, inactiveColor }}
      />
      <View style={styles.progressBarContainer}>
        <Slider
          minimumTrackTintColor={barActiveColor ?? activeColor}
          maximumTrackTintColor={barContainerColor}
          thumbTintColor={barActiveColor ?? activeColor}
          onSlidingStart={activateSlider}
          value={sliderCurrentValue}
          onSlidingComplete={(value) => changeProgress(value)}
          disabled={!status || !status.isLoaded}
        />
        {isTextProgressVisible && status.isLoaded && (
          <View style={styles.timeContainer}>
            <ThemedText2 fontSize={14} style={{ color: activeColor }}>
              {status.positionMillis !== undefined
                ? formatMillisecondsToAudioTime(status.positionMillis)
                : '--'}
            </ThemedText2>
            <ThemedText2 fontSize={14} style={{ color: activeColor }}>
              {soundDuration !== undefined && !isNaN(soundDuration)
                ? formatMillisecondsToAudioTime(soundDuration)
                : '--'}
            </ThemedText2>
          </View>
        )}
      </View>
      {isSlowButtonVisible && (
        <SlowButton
          style={styles.slowButton}
          activeColor={activeColor}
          inactiveColor={inactiveColor}
        />
      )}
    </View>
  );
}
